// FILE: src/context/AuthContext.js
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  onAuthStateChanged,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";

const normalizeRole = (role) => {
  const r = String(role || "").toLowerCase().trim();
  if (!r) return "member";
  if (r === "superadmin") return "admin";
  return r;
};

export const AuthContext = createContext({
  user: null,
  userProfile: null,
  role: null,
  tenantId: null,
  tenantConfig: null,
  loading: true,
  login: async () => {},
  signup: async () => {},
  logout: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [tenantConfig, setTenantConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log("[AuthContext] subscribing to onAuthStateChanged");

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log("[AuthContext] onAuthStateChanged:", firebaseUser?.uid || null);

      // Logged out
      if (!firebaseUser) {
        setUser(null);
        setUserProfile(null);
        setTenantConfig(null);
        setLoading(false);
        return;
      }

      setLoading(true);

      const baseUser = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName || null,
      };
      setUser(baseUser);

      try {
        // ✅ OPTION A ONLY: Role comes from Firestore users/{uid}
        const userRef = doc(db, "users", firebaseUser.uid);
        console.log("[AuthContext] reading:", userRef.path);

        const userSnap = await getDoc(userRef);

        let profileFromDb = null;

        if (userSnap.exists()) {
          profileFromDb = userSnap.data();
        } else {
          console.warn("[AuthContext] users doc missing. Creating safe default profile for:", firebaseUser.uid);

          // Create a minimal profile (member) WITHOUT breaking future admin upgrades
          const safeProfile = {
            uid: firebaseUser.uid,
            email: firebaseUser.email || "",
            role: "member",
            createdAt: serverTimestamp(),
          };

          await setDoc(userRef, safeProfile, { merge: true });
          profileFromDb = safeProfile;
        }

        const roleFromDb = normalizeRole(profileFromDb?.role);
        const computedTenantId = profileFromDb?.tenantId ?? null;

        console.log("[AuthContext] roleFromDb:", roleFromDb);
        console.log("[AuthContext] tenantId:", computedTenantId);

        // Save userProfile exactly from Firestore (+ normalized role)
        setUserProfile({
          ...(profileFromDb || {}),
          role: roleFromDb,
          tenantId: computedTenantId,
          profileLoaded: true,
        });

        // Tenants (optional)
        if (computedTenantId) {
          const tenantRef = doc(db, "tenants", computedTenantId);
          console.log("[AuthContext] reading tenant:", tenantRef.path);

          const tenantSnap = await getDoc(tenantRef);

          if (tenantSnap.exists()) {
            const t = tenantSnap.data();
            const enabledMenus = Array.isArray(t?.enabledMenus) ? t.enabledMenus : [];
            setTenantConfig({ id: computedTenantId, ...t, enabledMenus });
          } else {
            console.warn("[AuthContext] tenant doc not found for", computedTenantId);
            setTenantConfig({ id: computedTenantId, name: computedTenantId, enabledMenus: [] });
          }
        } else {
          setTenantConfig(null);
        }
      } catch (err) {
        console.error("[AuthContext] error fetching profile/tenant:", err);

        // Keep safe defaults
        setUserProfile((prev) =>
          prev
            ? { ...prev, profileLoaded: prev.profileLoaded ?? true }
            : { role: "member", tenantId: null, profileLoaded: false }
        );
        setTenantConfig(null);
      } finally {
        setLoading(false);
      }
    });

    return () => {
      console.log("[AuthContext] unsubscribing");
      unsubscribe();
    };
  }, []);

  async function logout() {
    await signOut(auth);
  }

  async function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  async function signup(email, password) {
    return createUserWithEmailAndPassword(auth, email, password);
  }

  const role = userProfile?.role ?? null;
  const tenantId = userProfile?.tenantId ?? null;

  const value = useMemo(
    () => ({
      user,
      userProfile,
      role,
      tenantId,
      tenantConfig,
      loading,
      login,
      signup,
      logout,
    }),
    [user, userProfile, role, tenantId, tenantConfig, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

export default AuthProvider;